import React from 'react';
import { ResponsiveContainer } from 'recharts';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import { useI18n } from '../../i18n/I18nContext';
import { formatCompact, formatNumber, formatShortDate } from '../../utils/datetime';

/**
 * One chart theme for every Recharts chart in the CRM (spec §3.15): Dashboard, OrderLists,
 * Statistics, CourseRevenue, CourseAnalytics, RiskScoreTrendChart. Colours are CSS tokens
 * only, so dark mode follows without a branch.
 *
 *   <ChartFrame data={rows} emptyTitle={t('dashboard.noRevenueData')}>
 *     <AreaChart data={rows} margin={CHART_MARGIN}>
 *       <defs><AreaGradient id="rev" color={seriesColor(0)} /></defs>
 *       <CartesianGrid {...GRID_PROPS} />
 *       <XAxis dataKey="date" {...X_AXIS_PROPS} tickFormatter={dateTick} />
 *       <YAxis {...Y_AXIS_PROPS} tickFormatter={compactTick} />
 *       <Tooltip content={<ChartTooltip valueFormatter={(v) => formatMoney(v)} />} cursor={CURSOR_PROPS} />
 *       <Area dataKey="revenue" {...areaProps(0, 'rev')} />
 *     </AreaChart>
 *   </ChartFrame>
 *
 * Rules the helpers encode: dashed horizontal grid only; no axis lines or ticks; 12px muted tick
 * text (not the "subtle" colour); area = 2.5px stroke + a 22% -> 0% gradient, no dots; bars
 * round the top corners (radius 8, max 40px wide); donut inner radius 60% with a 2deg gap and no
 * stroke; axis labels are compact numbers WITHOUT a currency (put the unit in the card subtitle);
 * an empty or single-point series is an <InlineEmpty>, never a lone dot. Series draw in once
 * (700ms) and not at all for a visitor who asked for reduced motion.
 */

/** Series order: yellow first, then the data tones. */
export const SERIES = [
  'var(--color-primary)',
  'var(--color-secondary)',
  'var(--color-teal)',
  'var(--color-coral)',
  'var(--color-success-fill)',
  'var(--color-info-fill)',
];

export const seriesColor = (index = 0) => SERIES[((index % SERIES.length) + SERIES.length) % SERIES.length];

/**
 * True when the visitor asked for less motion, or when the browser cannot say (jsdom, very old
 * browsers): the safe answer is "do not animate". Read at render time, never at import.
 */
export function prefersReducedMotion() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

/** Recharts animation props: one gentle draw-in, or none under reduced motion. */
export function chartMotion() {
  return { isAnimationActive: !prefersReducedMotion(), animationDuration: 700, animationEasing: 'ease-out' };
}

export const AXIS_TICK = { fontSize: 12, fill: 'var(--color-text-muted)' };
export const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };

/** <CartesianGrid {...GRID_PROPS} />: horizontal hairlines, dashed 3 3 */
export const GRID_PROPS = { stroke: 'var(--color-border)', strokeDasharray: '3 3', vertical: false };
/** <XAxis {...X_AXIS_PROPS} />: no axis line, no tick marks */
export const X_AXIS_PROPS = { tick: AXIS_TICK, axisLine: false, tickLine: false, tickMargin: 8, minTickGap: 16 };
/** <YAxis {...Y_AXIS_PROPS} />: same, with room for "800 ming" / "2.6 mln" (a count axis can pass a narrower `width`) */
export const Y_AXIS_PROPS = { tick: AXIS_TICK, axisLine: false, tickLine: false, tickMargin: 8, width: 72 };
/** <Tooltip cursor={CURSOR_PROPS} /> */
export const CURSOR_PROPS = { stroke: 'var(--color-border-strong)', strokeDasharray: '3 3' };
export const BAR_CURSOR_PROPS = { fill: 'var(--color-surface-hover)' };

/* Tick formatters take ONE argument: Recharts calls tickFormatter(value, index), and a
   formatter whose second parameter is a currency printed "2.6M 4". */
export const compactTick = (value) => formatCompact(value);
export const numberTick = (value) => formatNumber(value);
export const dateTick = (value) => formatShortDate(value);

/** Props for <Area>: 2.5px stroke, the gradient fill, no dots */
export function areaProps(index = 0, gradientId) {
  const color = seriesColor(index);
  return {
    type: 'monotone',
    stroke: color,
    strokeWidth: 2.5,
    fill: gradientId ? `url(#${gradientId})` : color,
    fillOpacity: gradientId ? 1 : 0.12,
    dot: false,
    activeDot: { r: 5, strokeWidth: 2, stroke: 'var(--color-surface)', fill: color },
    ...chartMotion(),
  };
}

/** Props for <Line>: the same stroke, no dots */
export function lineProps(index = 0) {
  const color = seriesColor(index);
  return {
    type: 'monotone',
    stroke: color,
    strokeWidth: 2.5,
    dot: false,
    activeDot: { r: 5, strokeWidth: 2, stroke: 'var(--color-surface)', fill: color },
    ...chartMotion(),
  };
}

/** Props for <Bar>: rounded tops, never wider than 40px */
export function barProps(index = 0) {
  return { fill: seriesColor(index), radius: [8, 8, 0, 0], maxBarSize: 40, ...chartMotion() };
}

/**
 * Props for <Pie>: a donut with a gap between slices and no outline. The motion keys are
 * getters, so `{...PIE_PROPS}` reads the visitor's motion preference when the chart renders.
 */
export const PIE_PROPS = {
  innerRadius: '60%',
  outerRadius: '92%',
  paddingAngle: 2,
  stroke: 'none',
  dataKey: 'value',
  get isAnimationActive() { return chartMotion().isAnimationActive; },
  animationDuration: 700,
  animationEasing: 'ease-out',
};

/** The soft fade under an area: 22% of the series colour at the top, 0% at the bottom. Put it in <defs>. */
export function AreaGradient({ id, color = seriesColor(0) }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={0.22} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  );
}

/**
 * The one tooltip: surface, hairline, radius 16, float shadow; a 12px muted label and a
 * 14/700 value per series. `content={<ChartTooltip valueFormatter={...} labelFormatter={...} />}`.
 */
export function ChartTooltip({ active, payload, label, valueFormatter = (v) => formatNumber(v), labelFormatter = (l) => l }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="ui-chart-tooltip" role="status">
      {label !== undefined && label !== null && label !== '' && (
        <p className="ui-chart-tooltip__label">{labelFormatter(label)}</p>
      )}
      {payload.map((item, i) => (
        <div key={`${item.dataKey || item.name || i}`} className="ui-chart-tooltip__row">
          <span className="ui-chart-tooltip__dot" style={{ '--ui-dot': item.color || item.payload?.fill || seriesColor(i) }} />
          {item.name !== undefined && item.name !== item.dataKey && <span className="ui-chart-tooltip__name">{item.name}</span>}
          <span>{valueFormatter(item.value, item)}</span>
        </div>
      ))}
    </div>
  );
}

/** Legend = chips with an 8px dot. items: [{ label, color? }] (colour defaults to the series order). */
export function ChartLegend({ items = [] }) {
  return (
    <ul className="ui-chart-legend">
      {items.map((item, i) => (
        <li key={item.label} style={{ '--ui-dot': item.color || seriesColor(i) }}>{item.label}</li>
      ))}
    </ul>
  );
}

/** True when a series is too short to draw: no data, or one lone point. */
export const isChartEmpty = (data, minPoints = 2) => !Array.isArray(data) || data.length < minPoints;

/**
 * The frame every chart sits in: 280px tall (220 on phones). When the series is empty or a
 * single point it renders an <InlineEmpty> instead (pass `emptyTitle`, optionally an `emptyKind`).
 * `minPoints={1}` for bar and donut charts, where one bar is a real chart.
 */
export function ChartFrame({ data, minPoints = 2, emptyTitle, emptyDescription, emptyKind = 'empty', className = '', children }) {
  const { t } = useI18n();
  if (isChartEmpty(data, minPoints)) {
    return <InlineEmpty kind={emptyKind} title={emptyTitle || t('ui.noDataAvailable')} description={emptyDescription} />;
  }
  return (
    <div className={`ui-chart ${className}`.trim()}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}
